/**
 * How much room is left in a field.
 *
 * `maxLength` on its own is a silent wall: typing simply stops, and the most
 * natural reading of that is a broken keyboard or a frozen page. Somebody
 * pasting a long policy title deserves to be told what happened.
 *
 * QUIET UNTIL IT MATTERS. Nothing renders while there is comfortable room —
 * a counter beside every field is noise that teaches people to stop reading
 * the things beside fields. It appears at four fifths of the limit, which is
 * late enough to stay out of the way and early enough to be a warning rather
 * than an epitaph.
 *
 * `role="status"` so a screen reader hears it when it appears and when the
 * field fills, rather than discovering the limit by pressing keys that do
 * nothing.
 */

import styles from './CharCount.module.css';

/** Show the count once this much of the field is used. */
export const SHOW_AT = 0.8;

export interface CharCountProps {
  value: string;
  max: number;
  /** Ties the count to its field for assistive technology. */
  id?: string;
  /**
   * Show "x of 2,000" from the first character, not only near the limit. For a
   * long field where the person is writing to a length, such as a description.
   */
  always?: boolean;
}

export function charCountLabel(used: number, max: number): string | null {
  if (used < Math.floor(max * SHOW_AT)) return null;
  if (used >= max) return `${max} character limit reached`;
  return `${max - used} characters left`;
}

/** "128 of 2,000": always shown, for a field a person writes to a length. */
export function charCountOf(used: number, max: number): string {
  return `${used.toLocaleString('en-US')} of ${max.toLocaleString('en-US')}`;
}

export function CharCount({ value, max, id, always = false }: CharCountProps) {
  if (always) {
    const full = value.length >= max;
    // Announced only when the field fills: a status that changes on every keystroke is noise to a screen reader.
    return (
      <p id={id} className={full ? `${styles.count} ${styles.full}` : styles.count} role={full ? 'status' : undefined}>
        {charCountOf(value.length, max)}{full ? ': limit reached' : ''}
      </p>
    );
  }

  // Count what the field holds, not what was typed: maxLength already stopped
  // anything past the limit.
  const label = charCountLabel(value.length, max);
  if (!label) return null;

  return (
    <p
      id={id}
      className={value.length >= max ? `${styles.count} ${styles.full}` : styles.count}
      role="status"
    >
      {label}
    </p>
  );
}
